import { isTimePermissionEnabled } from '../../config/applicationFeatures';
import React, { useMemo, useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { StatusBadge } from '../../components/StatusBadge';
import { getWorkPolicyModuleCapabilities } from '../../config/workPolicyCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import {
  deactivateWorkPolicy,
  getWorkPolicyDetails,
} from '../../services/workPolicyService';
import type { WorkPolicyDetails } from '../../types/workPolicy';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { formatMinutes } from '../../utils/dateTime';
import { shiftWorkPolicyErrorMessage } from '../../utils/shiftWorkPolicyErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'WorkPolicyDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

function minutesLabel(value: number | null | undefined): string | null {
  if (value == null) return null;
  const formatted = formatMinutes(value);
  return formatted === 'Not available' ? null : formatted;
}

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

export function WorkPolicyDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getWorkPolicyModuleCapabilities(auth.permissions);
  const { workPolicyId } = route.params;
  const [item, setItem] = useState<WorkPolicyDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const data = await getWorkPolicyDetails(workPolicyId);
        setItem(data);
        setError(null);
      } catch (caught) {
        setItem(null);
        setError(
          shiftWorkPolicyErrorMessage(
            caught,
            'The requested work policy could not be loaded.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [workPolicyId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.WORK_POLICY_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Work policy"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading work policy" />
        </AppScreen>
      ) : error && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Work policy"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : item ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title={item.policyName}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <StatusBadge label={item.statusLabel} />
          <AppCard style={styles.card}>
            {capabilities.canUpdateWorkPolicy &&
            isActiveConfigurationRecord(item.isActive) ? (
              <ConfigurationDeleteButton
                entityLabel="work policy"
                loading={softDelete.busy}
                disabled={softDelete.busy}
                onPress={() =>
                  softDelete.confirmAndDelete({
                    entityLabel: 'work policy',
                    recordName: item.policyName,
                    deleteRecord: async () => {
                      await deactivateWorkPolicy(item);
                    },
                    onSuccess: () => navigation.goBack(),
                    errorMessage: caught =>
                      shiftWorkPolicyErrorMessage(
                        caught,
                        'Unable to delete work policy.',
                      ),
                  })
                }
              />
            ) : null}
            <Field palette={palette} label="Policy code" value={item.policyCode} />
            <Field palette={palette} label="Policy name" value={item.policyName} />
            <Field palette={palette} label="Description" value={item.description} />
            <Field palette={palette} label="Location" value={item.locationName} />
            <Field
              palette={palette}
              label="Default shift"
              value={
                item.defaultShiftName ||
                (item.defaultShiftId ? String(item.defaultShiftId) : null)
              }
            />
            <Field
              palette={palette}
              label="Expected work duration"
              value={minutesLabel(item.expectedWorkMinutes)}
            />
            <Field
              palette={palette}
              label="Lunch break duration"
              value={minutesLabel(item.lunchBreakMinutes)}
            />
            <Field
              palette={palette}
              label="Spread duration"
              value={minutesLabel(item.spreadMinutes)}
            />
            <Field
              palette={palette}
              label="Minimum half day"
              value={minutesLabel(item.minimumHalfDayMinutes)}
            />
            <Field
              palette={palette}
              label="Late grace"
              value={minutesLabel(item.lateGraceMinutes)}
            />
            <Field
              palette={palette}
              label="Early exit grace"
              value={minutesLabel(item.earlyExitGraceMinutes)}
            />
            <Field
              palette={palette}
              label="Short hours tolerance"
              value={minutesLabel(item.shortHoursToleranceMinutes)}
            />
            <Field
              palette={palette}
              label="Maximum late"
              value={minutesLabel(item.maximumLateMinutes)}
            />
            <Field
              palette={palette}
              label="Maximum early exit"
              value={minutesLabel(item.maximumEarlyExitMinutes)}
            />
            <Field
              palette={palette}
              label="Working days per month"
              value={
                item.workingDaysPerMonth != null
                  ? `${item.workingDaysPerMonth} (fallback; monthly hours use Office Calendar working days)`
                  : null
              }
            />
            <Field
              palette={palette}
              label="Allow multiple punches"
              value={yesNo(item.allowMultiplePunches)}
            />
            <Field
              palette={palette}
              label="Allow manual attendance"
              value={yesNo(item.allowManualAttendance)}
            />
            <Field
              palette={palette}
              label="Allow attendance correction"
              value={yesNo(item.allowAttendanceCorrection)}
            />
            <Field palette={palette} label="WFH eligible" value={yesNo(item.wfhEligible)} />
            {isTimePermissionEnabled() ? <Field
              palette={palette}
              label="Permission enabled"
              value={yesNo(item.permissionEnabled)}
            /> : null}
            <Field palette={palette} label="Overtime enabled" value={yesNo(item.overtimeEnabled)} />
            <Field
              palette={palette}
              label="Overtime after"
              value={minutesLabel(item.overtimeAfterMinutes)}
            />
            <Field
              palette={palette}
              label="Maximum overtime"
              value={minutesLabel(item.maximumOvertimeMinutes)}
            />
            <Field
              palette={palette}
              label="Missing checkout policy"
              value={item.missingCheckoutPolicy}
            />
            <Field palette={palette} label="Default policy" value={yesNo(item.isDefault)} />
            <Field palette={palette} label="Status" value={item.statusLabel} />
          </AppCard>
          <View style={styles.actions}>
            {capabilities.canUpdateWorkPolicy ? (
              <AppButton
                title="Edit"
                onPress={() =>
                  navigation.navigate(routes.workPolicyEdit, { workPolicyId })
                }
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.md, marginTop: spacing.md },
  field: { gap: spacing.xs },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
});
